// Modules whose records fill templates (backend docs/template-data.md).
//
// docs' API says which modules the install turned on and where docs reads
// them for the screen (GET /embed/v1/template-sources → `{items: [{source,
// api_url: "/pass/<module>"}]}`): docs' own pass-through, on docs' origin.
// The record picker, the field catalogue and the values go there with docs'
// session cookie (@zehnmindai/embed's bffFetch), and docs-api asks the
// module as the person (their zehn-id token exchanged for that module), so
// nobody sees a value that module would not show them. No other module's
// token is ever in this page. Plain fetch, not docs' axios client: humps
// would camelize the field paths. Bodies stay snake_case.

import { bffFetch } from '@zehnmindai/embed/bff';
import baseAxios from './request';
import { TEMPLATE_SOURCES } from './urls';

// A loop rather than a trailing-slash regex, which static analysis flags as a backtracking risk.
const withoutTrailingSlashes = (value) => {
    let end = value.length;
    while (end > 0 && value[end - 1] === '/') end -= 1;
    return value.slice(0, end);
};

let cached = null; // promise of [{source, apiUrl}] for this tab

/** The enabled sources, read once per tab (a failed read is retried next time). */
export function fetchTemplateSources({ refresh = false } = {}) {
  if (!cached || refresh) {
    cached = Promise.resolve()
      .then(() => baseAxios({ method: 'GET', url: TEMPLATE_SOURCES }))
      .then((res) => (Array.isArray(res?.data?.items) ? res.data.items : []))
      .catch(() => {
        cached = null;
        return [];
      });
  }
  return cached;
}

/** `zehn-hr:employee` → {source: 'zehn-hr', entity: 'employee'} (null when not one). */
export function splitEntity(value) {
  const [source, entity] = String(value || '').split(':');
  return source && entity ? { source, entity } : null;
}

/** `zehn-hr:employee.full_name` → {source, path}; docs' own bindings → {source: 'docs', path}. */
export function splitBinding(binding) {
  const at = String(binding || '').indexOf(':');
  if (at <= 0) return null;
  return { source: binding.slice(0, at), path: binding.slice(at + 1) };
}

// docs' own bindings: filled when the document is registered in a journal.
export const DOCS_BINDINGS = ['docs:registration.number', 'docs:registration.date'];

/** Template language tag (uz-Latn, uz-Cyrl, ru, en, or a UI code) → the `lang` a module formats for. */
export function sourceLang(tag, uiLanguage) {
  const value = tag || uiLanguage || 'en';
  if (value === 'uz' || value === 'uz-Latn') return 'uz-Latn';
  if (value === 'uz-Cyrl' || value === 'ru' || value === 'en') return value;
  return 'en';
}

/** docs' pass-through for `source`: a path on docs' own origin (`/pass/zehn-hr`), never another origin. */
function passBase(source, apiUrl) {
  const path = typeof apiUrl === 'string' && apiUrl.startsWith('/') && !apiUrl.startsWith('//') ? apiUrl : `/pass/${source}`;
  return withoutTrailingSlashes(path);
}

/** Calls to one module, as the person, through docs' pass-through. */
export function sourceApi(source, apiUrl) {
  const base = passBase(source, apiUrl);
  const call = async (path, init = {}) => {
    const response = await bffFetch(`${base}/embed/v1/template-data${path}`, {
      ...init,
      headers: {
        Accept: 'application/json',
        ...(init.body ? { 'Content-Type': 'application/json' } : {}),
      },
    });
    if (!response.ok) {
      throw Object.assign(new Error(`${source} answered ${response.status}`), { status: response.status });
    }
    return response.json();
  };
  return {
    source,
    available: true,
    catalogue: (lang) => call(`/catalogue?lang=${encodeURIComponent(lang || '')}`),
    search: (entity, q, limit = 20) =>
      call(`/${encodeURIComponent(entity)}?q=${encodeURIComponent(q || '')}&limit=${limit}`),
    values: (entity, id, paths, lang) =>
      call(`/${encodeURIComponent(entity)}/${encodeURIComponent(id)}/values`, {
        method: 'POST',
        body: JSON.stringify({ paths, lang }),
      }),
  };
}

/**
 * The API for `source`, or why it can't be used: `not_enabled` (the install
 * hasn't turned it on, or docs-api has no address for that module).
 */
export async function resolveSource(source) {
  const sources = await fetchTemplateSources();
  const found = sources.find((s) => s.source === source);
  if (!found) return { reason: 'not_enabled', api: null };
  return { reason: null, api: sourceApi(source, found.apiUrl) };
}

/** Placeholders bound to `source`: [{key, path}]. */
export function boundToSource(items, source) {
  return (items || [])
    .map((item) => ({ key: item.key, bound: splitBinding(item.binding) }))
    .filter(({ bound }) => bound && bound.source === source)
    .map(({ key, bound }) => ({ key, path: bound.path }));
}

/** Test seam: forget the cached sources list. */
export function resetTemplateSourcesCache() {
  cached = null;
}
